import memoize from 'fast-memoize';
import { toSql } from 'pgsql-ast-parser/lib';
import { format } from 'sql-formatter';
import { DataFieldWithDataType } from '../../types';
import { isHierarchical } from '../../utils';
import { hierarchicalfieldToExpr, nonHierarchicalfieldToExpr } from './utils';

/**
 * Internal SQL formatter function (unmemoized)
 */
const formatSqlInternal = (sql: string): string => {
  try {
    return format(sql, {
      language: 'postgresql',
      keywordCase: 'upper',
      dataTypeCase: 'lower',
      functionCase: 'lower',
      tabWidth: 2,
      linesBetweenQueries: 1,
    });
  } catch (e) {
    console.error('SQL formatting error:', e);
    return sql;
  }
};

/**
 * Formats SQL code with PostgreSQL dialect and consistent styling.
 * Results are memoized for performance - repeated formatting of the same SQL is cached.
 * @param sql - The SQL string to format
 * @returns Formatted SQL string, or original SQL if formatting fails
 */
export const formatSql = memoize(formatSqlInternal);

/**
 * Escapes a string for safe use in PostgreSQL string literals.
 * - Escapes single quotes
 * - Removes newlines and carriage returns completely as the backend parser doesn't support them
 *
 * @param str The string to escape
 * @returns The escaped string safe for use in PostgreSQL string literals
 */
export const escapeSqlString = (str: string): string => {
  if (str === null || str === undefined) {
    return '';
  }

  return str
    .replace(/'/g, "''") // Escape single quotes
    .replace(/[\n\r]/g, ''); // Remove newlines and carriage returns completely since the backend parser doesn't support them
};

export const fieldToSql = (dataField: DataFieldWithDataType): string => {
  return toSql.selectionColumn({
    expr: isHierarchical(dataField)
      ? hierarchicalfieldToExpr(dataField)
      : nonHierarchicalfieldToExpr(dataField),
  });
};

export namespace SQL {
  export type Select = string;
  export type From = string;
  export type Where = (string | null)[];
  export type GroupBy = string;
  export type Limit = string;
  export type Offset = string;

  export interface Query {
    select: SQL.Select;
    from: SQL.From;
    where?: SQL.Where;
    groupBy?: SQL.GroupBy;
    limit?: SQL.Limit;
    offset?: SQL.Offset;
  }

  export const queryToString = (query: Query): string => {
    const queryWhere = query.where?.filter((s) => s !== null);
    const where =
      queryWhere && queryWhere.length > 0
        ? `WHERE ${queryWhere.join(' AND ')}`
        : '';
    const groupBy = query.groupBy ? `GROUP BY ${query.groupBy}` : '';
    return `
      SELECT ${query.select}
        FROM ${query.from}
        ${where}
        ${groupBy}
    `;
  };
}
